import { useEffect, useMemo } from 'react'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import GeoJSON from 'ol/format/GeoJSON'
import * as olExtent from 'ol/extent'

import Style from 'ol/style/Style'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Circle from 'ol/style/Circle'

import PropTypes from 'prop-types'

export default function PreviewMapLayer({
  map,
  metaData,
  style,
  zIndex,
  onLayerCreated,
  onLayerRemoved,
  uploaded,
}) {
  const features = useMemo(() => {
    const filteredFeatures = metaData.features.reduce((filtered, data) => {
      if (!filtered[data.properties.final_capture_id]) {
        filtered[data.properties.final_capture_id] = data
      }
      return filtered
    }, {})

    const filteredMeta = {
      type: 'FeatureCollection',
      features: Object.values(filteredFeatures),
    }

    const featuresFromMetadata = new GeoJSON().readFeatures(filteredMeta, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    })

    featuresFromMetadata.forEach(feature => {
      if (feature.getProperties().filename) {
        feature.setId(feature.getProperties().final_capture_id)
      }
    })

    return featuresFromMetadata
  }, [metaData])

  const [source, layer] = useMemo(() => {
    const source = new VectorSource()

    const layer = new VectorLayer({
      source,
      zIndex,
    })

    return [source, layer]
  }, [zIndex])

  useEffect(() => {
    map.addLayer(layer)
    onLayerCreated(layer)

    return () => {
      map.removeLayer(layer)
      onLayerRemoved(layer)
    }
  }, [map, layer, onLayerCreated, onLayerRemoved])

  useEffect(() => {
    source.clear()
    source.addFeatures(features)

    const extent = source.getExtent()
    const view = map.getView()
    const isEmpty = olExtent.isEmpty(extent)
    const extentsIntersect =
      !isEmpty && olExtent.intersects(extent, view.calculateExtent())
    const extentIsTooSmallInView =
      !isEmpty &&
      view.getResolutionForExtent(extent) / view.getResolution() < 1 / 32

    if (!isEmpty && (!extentsIntersect || extentIsTooSmallInView)) {
      view.fit(extent, {
        padding: [100, 400, 100, 400],
        maxZoom: 18,
        duration: 1000,
      })
    }
  }, [map, source, features])

  useEffect(() => {
    if (layer) {
      layer.setStyle(feature => {
        if (uploaded.has(feature.getId())) {
          return uploadedStyle
        } else {
          return style
        }
      })
    }
  }, [layer, style, uploaded])

  return <></>
}

const uploadedStyle = new Style({
  image: new Circle({
    radius: 6,
    fill: new Fill({ color: '#46C13C' }),
    stroke: new Stroke({ color: 'white', width: 2 }),
  }),
})

PreviewMapLayer.propTypes = {
  map: PropTypes.shape({
    addLayer: PropTypes.func,
    removeLayer: PropTypes.func,
    getView: PropTypes.func,
  }),
  style: PropTypes.object,
  metaData: PropTypes.object,
  zIndex: PropTypes.number,
  onLayerCreated: PropTypes.func,
  onLayerRemoved: PropTypes.func,
  uploaded: PropTypes.instanceOf(Set),
}
